import React, { useMemo } from 'react';
import { Button, Col, Row, Select } from 'antd';
import moment, { Moment } from 'moment';
import { useQueryFilter } from 'common/hooks/useQueryFilter';
import CustomDatePicker from 'common/components/DateTime/CustomDatePicker';
import { rangeConfig } from 'common/helpers/date.helper';
import { DATE_FORMAT } from 'common/const/common.const';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getFranchiseeStatisticsCSV } from 'app/store/reducers/statistic.reducer';
import { StoreSelector } from 'entities/Store/components/Selector/StoreSelector';
import { IStoreModel, selectorStoreAllKeyId } from 'entities/Store/Store.models';
import {
  EBooleanSelectText,
  EPaymentMetod,
  EPaymentMetodText,
  EStatisticsTab,
  EStatisticStoreOrderStatus,
  EStatisticStoreOrderStatusText,
} from 'entities/Statistic/Statistic.models';
import { franchiseeOrdersTableConfig } from 'entities/Statistic/Statistics.const';
import { FranchiseeOrdersTable } from 'entities/Statistic/components/FranchiseeOrdersTable';

export interface IFranchiseeSalesFilter {
  tab?: string;
  pageIndex: number;
  store?: Partial<IStoreModel>;
  dateFrom?: string;
  dateTo?: string;
  statuses?: EStatisticStoreOrderStatus[];
  promoUsed?: boolean;
  bitecoinsUsed?: boolean;
  paymentMethod?: string;
  franchiseeId?: string;
}

export const StatisticsSalesTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { authModel } = useAppSelector((store) => store.auth);

  const defaultFilter: IFranchiseeSalesFilter = useMemo(
    () => ({
      tab: EStatisticsTab.Sales,
      pageIndex: 1,
      store: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      statuses: undefined,
      promoUsed: undefined,
      bitecoinsUsed: undefined,
      paymentMethod: undefined,
    }),
    []
  );

  const { filter, updateFilter } = useQueryFilter(defaultFilter);

  const defaultDateValue: [Moment, Moment] | undefined =
    filter.dateFrom && filter.dateTo ? [moment(filter.dateFrom), moment(filter.dateTo)] : undefined;
  const statusSelectValue = useMemo(() => {
    if (!filter.statuses?.length) {
      return undefined;
    }

    if (filter.statuses.every((s) => s.includes('rejected'))) {
      return EStatisticStoreOrderStatus.CanceledByCustomer;
    }

    return filter.statuses[0];
  }, [filter.statuses]);
  const defaultPromoUsedValue = filter.promoUsed === undefined ? undefined : filter.promoUsed;
  const defaultBitecoinsUsedValue = filter.bitecoinsUsed === undefined ? undefined : filter.bitecoinsUsed;
  const onStoreChange = (item?: IStoreModel) => {
    const isAll = item?.id === selectorStoreAllKeyId;
    const store = item && !isAll ? { id: item?.id, storeName: item?.storeName } : undefined;

    updateFilter({ store });
  };

  const onDateChange = (value: any[] | null) => {
    if (value?.length) {
      const dateFrom = moment(value[0]).format();
      const dateTo = moment(value[1]).endOf('day').format();

      updateFilter({ dateFrom, dateTo });
    } else {
      updateFilter({ dateFrom: undefined, dateTo: undefined });
    }
  };

  const onOrderStatusChange = (value: EStatisticStoreOrderStatus) => {
    if (!value) {
      updateFilter({ statuses: undefined });

      return;
    }

    const newStatuses =
      value === EStatisticStoreOrderStatus.CanceledByCustomer
        ? [EStatisticStoreOrderStatus.CanceledByCustomer, EStatisticStoreOrderStatus.CanceledByStore]
        : [value];

    updateFilter({ statuses: newStatuses });
  };

  const onPromoUsedChange = (value: boolean) => {
    updateFilter({ promoUsed: value });
  };

  const onCoinsUsedChange = (value: boolean) => {
    updateFilter({ bitecoinsUsed: value });
  };

  const onPaymentMethodChange = (value: string) => {
    updateFilter({ paymentMethod: value });
  };

  const onExportCSV = () => {
    if (authModel?.access.userId) {
      dispatch(getFranchiseeStatisticsCSV({ ...filter, franchiseeId: authModel.access.userId }));
    }
  };

  return (
    <div>
      <Col span={24} className="mt-5 mb-5">
        <div className="fs-xxs font-family__default mb-3">Store</div>
        <StoreSelector
          onChange={onStoreChange}
          value={filter.store}
          defaultOpt={{ id: selectorStoreAllKeyId }}
          placeholder="Enter store name"
          allowClear={false}
        />
      </Col>
      <Col span={24}>
        <div className="fs-xxs font-family__default mb-3">Time range</div>
        <CustomDatePicker.RangePicker
          className="width-full franchisee__date-picker"
          value={defaultDateValue}
          presets={rangeConfig}
          getPopupContainer={(triggerNode) => triggerNode as HTMLElement}
          format={DATE_FORMAT}
          onChange={onDateChange}
          showTime={false}
        />
      </Col>
      <Row className="mt-5 mb-5">
        <Col span={6}>
          <Select
            className="width-full"
            placeholder="Order Status"
            onChange={onOrderStatusChange}
            allowClear
            value={statusSelectValue}
          >
            <Select.Option value={EStatisticStoreOrderStatus.New} label={EStatisticStoreOrderStatusText.New}>
              {EStatisticStoreOrderStatusText.New}
            </Select.Option>
            <Select.Option
              value={EStatisticStoreOrderStatus.PaymentAwaiting}
              label={EStatisticStoreOrderStatusText.PaymentAwaiting}
            >
              {EStatisticStoreOrderStatusText.PaymentAwaiting}
            </Select.Option>
            <Select.Option
              value={EStatisticStoreOrderStatus.PaymentCancelled}
              label={EStatisticStoreOrderStatusText.PaymentCancelled}
            >
              {EStatisticStoreOrderStatusText.PaymentCancelled}
            </Select.Option>
            <Select.Option
              value={EStatisticStoreOrderStatus.Preparing}
              label={EStatisticStoreOrderStatusText.Preparing}
            >
              {EStatisticStoreOrderStatusText.Preparing}
            </Select.Option>
            <Select.Option value={EStatisticStoreOrderStatus.Ready} label={EStatisticStoreOrderStatusText.Ready}>
              {EStatisticStoreOrderStatusText.Ready}
            </Select.Option>
            <Select.Option
              value={EStatisticStoreOrderStatus.Completed}
              label={EStatisticStoreOrderStatusText.Completed}
            >
              {EStatisticStoreOrderStatusText.Completed}
            </Select.Option>
            <Select.Option
              value={EStatisticStoreOrderStatus.CanceledByCustomer}
              label={EStatisticStoreOrderStatusText.Canceled}
            >
              {EStatisticStoreOrderStatusText.Canceled}
            </Select.Option>
          </Select>
        </Col>
        <Col span={6}>
          <Select
            className="width-full"
            placeholder="Promo used"
            onChange={onPromoUsedChange}
            value={defaultPromoUsedValue}
            allowClear
          >
            <Select.Option value={true} label={EBooleanSelectText.Yes}>
              {EBooleanSelectText.Yes}
            </Select.Option>
            <Select.Option value={false} label={EBooleanSelectText.No}>
              {EBooleanSelectText.No}
            </Select.Option>
          </Select>
        </Col>
        <Col span={6}>
          <Select
            className="width-full"
            placeholder="Coins used"
            onChange={onCoinsUsedChange}
            value={defaultBitecoinsUsedValue}
            allowClear
          >
            <Select.Option value={true} label={EBooleanSelectText.Yes}>
              {EBooleanSelectText.Yes}
            </Select.Option>
            <Select.Option value={false} label={EBooleanSelectText.No}>
              {EBooleanSelectText.No}
            </Select.Option>
          </Select>
        </Col>
        <Col span={6}>
          <Select
            className="width-full"
            placeholder="Paid via"
            onChange={onPaymentMethodChange}
            value={filter.paymentMethod}
            allowClear
          >
            <Select.Option value={EPaymentMetod.Gcash} label={EPaymentMetodText.Gcash}>
              {EPaymentMetodText.Gcash}
            </Select.Option>
            <Select.Option value={EPaymentMetod.GrabPay} label={EPaymentMetodText.GrabPay}>
              {EPaymentMetodText.GrabPay}
            </Select.Option>
            <Select.Option value={EPaymentMetod.Maya} label={EPaymentMetodText.Maya}>
              {EPaymentMetodText.Maya}
            </Select.Option>
            <Select.Option value={EPaymentMetod.Bitecoins} label={EPaymentMetodText.Bitecoins}>
              {EPaymentMetodText.Bitecoins}
            </Select.Option>
          </Select>
        </Col>
      </Row>
      <Col span={6}>
        <Button className="width-full mb-5" onClick={onExportCSV}>
          Export CSV
        </Button>
      </Col>
      <Row>
        <FranchiseeOrdersTable config={franchiseeOrdersTableConfig} filter={filter} updateFilter={updateFilter} />
      </Row>
    </div>
  );
};
